/**
 * Tożsamość pozycji koszyka.
 *
 * Pozycję identyfikuje trójka: typ, identyfikator obiektu i wariant. Sam identyfikator
 * obiektu nie wystarcza z dwóch niezależnych powodów: ten sam objectId może wystąpić
 * w dwóch typach produktu, a dwie edycje tej samej pracy mają ten sam objectId i ten
 * sam typ, choć są dwoma produktami o różnych cenach. Dopasowanie po samym objectId
 * zlepiało je w jedną pozycję — druga edycja nadpisywała pierwszą, a usunięcie jednej
 * zabierało obie.
 *
 * Trójka jest tą samą tożsamością, którą stosuje backend przy dokładaniu pozycji do
 * koszyka i przy składaniu zamówienia; rozjazd między stronami dawałby koszyk, którego
 * żądanie nie potrafi zestawić z pozycjami zapisanymi w bazie.
 */

/** Typ pozycji przyjmowany, gdy pozycja go nie nosi — ta sama wartość domyślna, co po stronie backendu. */
export const DEFAULT_CART_ITEM_TYPE = 'inventory_work';

/**
 * Wyróżnik pozycji bez wariantu. Pusty fragment klucza zlepiałby pracę w trybie prostym
 * z pozycją wariantową o tym samym objectId.
 */
const BEZ_WARIANTU = 'bez-wariantu';

export interface CartItemIdentity {
  objectId: number | string;
  type?: string;
  variantId?: number | string | null;
}

/**
 * Klucz tożsamości pozycji.
 *
 * Składniki sprowadzamy do tekstu, bo ta sama pozycja wraca raz z liczbowym, raz
 * z tekstowym identyfikatorem: koszyk gościa przechodzi przez JSON w pamięci
 * przeglądarki, a ładunki API przez dwa interceptory.
 *
 * @param item pozycja koszyka albo sama tożsamość
 * @return klucz porównywalny znak po znaku
 */
export function cartItemKey(item: CartItemIdentity): string {
  const typ = item.type ?? DEFAULT_CART_ITEM_TYPE;
  const wariant = item.variantId === null || item.variantId === undefined || item.variantId === ''
    ? BEZ_WARIANTU
    : String(item.variantId);

  return [typ, String(item.objectId), wariant].join('|');
}

/**
 * Czy obie pozycje opisują ten sam produkt.
 *
 * @param item pozycja koszyka albo sama tożsamość
 * @param other pozycja koszyka albo sama tożsamość
 * @return prawda, gdy pozycje mają wspólny klucz tożsamości
 */
export function isSameCartItem(item: CartItemIdentity, other: CartItemIdentity): boolean {
  return cartItemKey(item) === cartItemKey(other);
}

/**
 * Pozycja o wskazanej tożsamości na liście pozycji koszyka.
 *
 * @param items lista pozycji koszyka
 * @param identity szukana tożsamość
 * @return indeks pozycji albo -1, gdy pozycji nie ma
 */
export function findCartItemIndex(items: CartItemIdentity[], identity: CartItemIdentity): number {
  const klucz = cartItemKey(identity);

  return (items ?? []).findIndex((item) => cartItemKey(item) === klucz);
}

/**
 * Tożsamość pozycji zwróconej przez feed koszyka.
 *
 * Feed nie ma pola `objectId`: jego `id` jest identyfikatorem wiersza tłumaczenia
 * (złączenie nadpisuje kolumnę), a identyfikator pracy siedzi w `inventoryWorkId`.
 * Dopasowanie po `id` trafiałoby więc w zupełnie inny obiekt.
 *
 * @param item pozycja z odpowiedzi feedu koszyka
 * @return tożsamość porównywalna z pozycją koszyka
 */
export function feedItemIdentity(item: { inventoryWorkId?: number | string; type?: string; variantId?: number | string | null }): CartItemIdentity {
  return {
    objectId: item?.inventoryWorkId,
    type: item?.type,
    variantId: item?.variantId ?? null,
  };
}
